import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	getCardGradientStatement,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import TestEntityButton from './TestEntityButton';
import {
	ArgumentDeclarationState,
	ChangeSet,
	ChangeSetEntityChangeType,
	createStateFromTypeAndSubscribe,
	EntityType,
	FunctionDeclarationState,
	ProjectState,
	ValueDescriptorParentChildRelation,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	removeDisabledReasons,
	addDisabledReasons,
	READABLE_ENTITY_TYPES,
	getEntityArgumentDeclarations,
	getRelatedCanvasElementsOnTheRight,
	lowercaseFirstLetter,
	BUILT_IN_BASE_ENTITY_IDS,
	DataTypeState,
	IPrimitiveEntityTransfer,
	PrimitiveEntityState,
	StateMutationAction,
  safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import ArgumentDeclaration from './ArgumentDeclaration';
import './collapse-entity-button.css';
import CollapseEntityButton from './CollapseEntityButton';
import LogicScopeCallerNode from './LogicScopeCallerNode';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import { useNodeDescriptionPopup } from './useNodeDescriptionPopup';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import NewCardOutputButton from './new-card-output-button/NewCardOutputButton';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import { ConnectionNodeType, EditorService } from '../../services/editor';
import { findNextAvailableName } from '../../services/editor/create/utils';
import EntityLockedIconMenu from './entity-locked-icon/EntityLockedIcon';
import { Logger } from '@elyx-code/common-ts-utils';
import { popupNotification } from '../../popup-notification';

interface IFunctionDeclarationCardProps extends ICanvasElementComponentProps {
	entity: FunctionDeclarationState;
}

const FunctionDeclarationCard = (props: IFunctionDeclarationCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useNodeDescriptionPopup({
		entity: props.entity,
		canvasElement: props.canvasElement,
		project: props.project
	});

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-small-entity-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		(popup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-small-entity-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this trigger'
								: 'This trigger cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas--function-declaration-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								Logger.log(
									'Navigating to: ',
									`${props.project?.editorBasePath}/entity/${EntityType.FunctionDeclaration}/${props.entity.id}`
								);
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.FunctionDeclaration}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip: !entity.editable
								? `This ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)} has been locked and can no longer be edited`
								: `Edit this ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)}`,
							parentCanvasObject: popup.self,
							id:
								'canvas--function-declaration-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.clearEntityTestValues(props.entity);
							},
							label: 'Clear test values',
							iconEnd: 'fa-solid fa-eraser',
							disabled: !props.project.hasTestValuesForEntity(props.entity),
							tooltip: props.project.hasTestValuesForEntity(props.entity)
								? 'Clear all test values (purple overrides) for this node'
								: 'No test values to clear for this node',
							parentCanvasObject: popup.self,
							id:
								'canvas--function-declaration-card--' +
								props.entity.id +
								'--clear-test-values'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateFuncDecl
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		)
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				// @ts-ignore
				props.entity
			);

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(currentReasons, [
						CanvasEntityTestingDisabledReason.CannotBeAddedToTest
					]);
					return config.reasons;
				});
				hasUpdated = true; // Mark as updated
			}

			if (
				(props.project.test?.hasEntity(props.entity) ||
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				setDisabledReasons((currentReasons) => {
					const config = removeDisabledReasons(currentReasons, [
						CanvasEntityTestingDisabledReason.CannotBeAddedToTest
					]);
					return config.reasons;
				});
				hasUpdated = true;
			}

			if (!hasUpdated) {
				forceUpdate();
			}
		};

		const handleTestSetupStarted = () => {
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	useEffect(() => {
		props.canvasElement.draggableObject.updateConnections();
	});

	const entity = props.entity;

	const argumentDeclarations = getEntityArgumentDeclarations(entity);
	const children = getRelatedCanvasElementsOnTheRight(props.entity);

	const classes = [
		'canvas-draggable-card',

		// If it is a suggested entity, and the parent isn't already marked as 'suggested', we add the 'suggestion' class
		...(entity.suggestion && !(entity.parent as any).suggestion
			? ['suggestion']
			: [])
	];

	const canInplementNewArguments =
		!props.entity.implements ||
		!!props.entity.implements.inputs.find(
			(input) =>
				input.parentRelationType ===
				ValueDescriptorParentChildRelation.DynamicArgument
		);

	const showNewOutputButton =
		canInplementNewArguments &&
		!props.project.test &&
		!disabledReasons.includes(
			CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
		) &&
		!disabledReasons.includes(CanvasEntityOtherDisabledReason.Suggestion) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
		) &&
		!disabledReasons.includes(
			CanvasEntityTestingDisabledReason.CannotBeAddedToTest
		) &&
		(!props.project.onGoingConnectionNodeType ||
			(props.project.onGoingConnectionNodeType &&
				props.project.onGoingConnectionNodeType ===
					ConnectionNodeType.PropertyIn)) &&
		!disabledReasons.includes(
			CanvasEntityConnectionDisabledReason.NotInScope
		);

	return (
		<div
			ref={rootElementRef}
			className={classes.join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<TestEntityButton
				entity={props.entity}
				canvasElement={props.canvasElement}
				project={props.project}
				parentDisabledReasons={disabledReasons}
			/>
			{children.length > 0 && (
				<CollapseEntityButton
					entity={props.entity}
					project={props.project}
					canvasElement={props.canvasElement}
					parentDisabledReasons={disabledReasons}
				/>
			)}
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className="canvas-draggable-card__header"
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={parse(
						getCardGradientStatement(entity, {
							state: fromDisabledReasonsToCardState(
								disabledReasons
							)
						})
					)}
				>
					<div className="canvas-draggable-card__header__left-block">
						<i
							className={[
								'canvas-draggable-card-header__icon',
								'fa-solid',
								`fa-${ENTITY_FA_ICON_NAMES[entity.type] || 'rectangle-list'}`
							].join(' ')}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>
								{READABLE_ENTITY_TYPES[entity.type]?.singular}
							</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						<EntityLockedIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<LogicErrorIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<Button
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							size="small"
							ref={canvasPopup.anchorRef}
							className="canvas-small-entity-card-menu-button"
							id={`canvas-small-entity-card-menu-button-${entity.id}`}
							disabled={!!disabledReasons.length}
							onClick={(e: any) => {
								e.stopPropagation();
								canvasPopup?.toggle();
							}}
							onDoubleClick={(
								e: React.MouseEvent<HTMLButtonElement>
							) => {
								e.stopPropagation();
								if (disabledReasons.length) return;
								props.project?.canvas?.unselectAll();
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.FunctionDeclaration}/${props.entity.id}`
								);
							}}
						/>
					</div>
					<LogicScopeCallerNode
						draggableObject={props.canvasElement.draggableObject}
						entity={props.entity}
						project={props.project}
						parentDisabledReasons={disabledReasons}
					/>
				</header>
				<div className="canvas-draggable-card__content">
					{argumentDeclarations.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{argumentDeclarations
								.sort((a, b) => {
									// Sort by `.index` property
									if (a.index < b.index) {
										return -1;
									}
									if (a.index > b.index) {
										return 1;
									}
									return 0;
								})
								.map((argumentDeclaration) => (
									<ArgumentDeclaration
										key={argumentDeclaration.id}
										argumentDeclaration={
											argumentDeclaration
										}
										entity={props.entity}
										canvasElement={props.canvasElement}
										project={props.project}
										parentDisabledReasons={disabledReasons}
									/>
								))}
						</ul>
					)}
					{showNewOutputButton && (
						<div className="canvas-draggable-card__action-alt align-right">
							<NewCardOutputButton
								iconEnd="fa-solid fa-plus"
								size="small"
								parentDisabledReasons={disabledReasons}
								entity={entity}
								project={props.project}
								canvasElement={props.canvasElement}
								onClick={() => {
									const changeSet =
										props.project.logic.addChangeSet(
											new ChangeSet(
												props.project.logic,
												ProjectState.sessionAuthor,
												new Date().toISOString(),
												entity,
												false,
												StateMutationAction.AddInputToFuncDecl
											)
										);

									const newEntityTransfer =
										ArgumentDeclarationState.new(changeSet);
									newEntityTransfer.parent =
										entity.toReference();
									newEntityTransfer.name =
										findNextAvailableName(
											'Input',
											entity.inputs
										);

									const newEntity =
										createStateFromTypeAndSubscribe(
											newEntityTransfer,
											props.project.logic
										) as ArgumentDeclarationState;

									newEntity.hydrateAncestors();
									newEntity.addSelfToProject(changeSet);

									changeSet.add(
										newEntity,
										ChangeSetEntityChangeType.Added
									);

									newEntity.initChildren(changeSet);

									const builtInPrimitiveStringEntity =
										props.project.logic.getBuiltIn(
											BUILT_IN_BASE_ENTITY_IDS[
												'primitive-entity'
											].string.id
										) as PrimitiveEntityState;

									// Add 'string' data-type as default
									const dataTypeTransfer =
										DataTypeState.new(changeSet);
									dataTypeTransfer.parent =
										newEntity.toReference();
									dataTypeTransfer.entity =
										(builtInPrimitiveStringEntity?.toJSON() as IPrimitiveEntityTransfer) ||
										null;

									const dataTypeInstance =
										createStateFromTypeAndSubscribe(
											dataTypeTransfer,
											props.project.logic
										) as DataTypeState;

									dataTypeInstance.hydrateAncestors();
									dataTypeInstance.addSelfToProject(
										changeSet
									);
									changeSet.add(
										dataTypeInstance,
										ChangeSetEntityChangeType.Added
									);

									dataTypeInstance.initChildren(changeSet);

									EditorService.navigate(
										`${props.project?.editorBasePath}/entity/${EntityType.ArgumentDeclaration}/${newEntity.id}`
									);

									props.project.renderAndCloseChangeSet(
										changeSet
									);
								}}
							>
								Input
							</NewCardOutputButton>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default FunctionDeclarationCard;
